import { useEffect, useMemo } from "react";
import { Col, Row } from "src/components/layout";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { month_data, year_data } from "src/constants";
import { googlePlacesFieldProps } from "src/constants/googlePlaces";
import {
  type PreviousAddressFormData,
  toCreatePreviousAddressReq,
  toUpdatePreviousAddressReq,
} from "src/api/adapters/address";
import {
  AddressFields,
  FormTable,
  FormTableBody,
  Location06Icon,
  Select,
  SelectItem,
  SectionTitle,
} from "@porchpass/ui";
import { DomainAddressType } from "@porchpass/api-client";
import {
  addressErrorsAfterSubmit,
  fromUiAddressPatch,
  toUiAddress,
} from "src/forms/adapters/address";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import {
  notEmpty,
  lessThanMaxLength,
  validStreetAddress,
  validZipcode,
  validDateRange,
  validate,
  test,
  type ValidationMapperFunc,
} from "src/utils";

const validationMapper: ValidationMapperFunc<PreviousAddressFormData> = (
  data,
) => ({
  street1: [
    test(notEmpty, "Address must not be empty"),
    test(validStreetAddress, "Must be a valid street address"),
    test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
  ],
  city: [
    test(notEmpty, "City must not be empty"),
    test(lessThanMaxLength(70), "City must not exceed 70 characters"),
  ],
  state: [test(notEmpty, "State must not be empty")],
  zip_code: [
    test(notEmpty, "Zip Code must not be empty"),
    test(validZipcode, "Must be a valid US zip code"),
  ],
  from_month: [
    test(notEmpty, "Must select a month"),
    test(
      () =>
        validDateRange(
          data.from_month ?? "",
          data.from_year ?? "",
          (new Date().getMonth() + 1).toString().padStart(2, "0"),
          new Date().getFullYear().toString(),
        ),
      "Start date must be in the past",
    ),
  ],
  from_year: [test(notEmpty, "Must select a year")],
  to_month: [test(notEmpty, "Must select a month")],
  to_year: [
    test(notEmpty, "Must select a year"),
    test(
      () =>
        validDateRange(
          data.from_month ?? "",
          data.from_year ?? "",
          data.to_month ?? "",
          data.to_year ?? "",
        ),
      "End date must be after start date",
    ),
  ],
});

type PreviousAddressModalProps = {
  address?: PreviousAddressFormData & { id?: string };
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  address,
  closeHandler,
  confirmHandler,
}: PreviousAddressModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<PreviousAddressFormData>(
    { ...address },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: PreviousAddressFormData) =>
      address?.id
        ? api.updateAddress(address.id, toUpdatePreviousAddressReq(data))
        : api.createAddress(toCreatePreviousAddressReq(data)),
    );

  useEffect(() => {
    if (response) {
      confirmHandler();
    }
  }, [response]);

  const formErrors = validate<PreviousAddressFormData>(
    formData,
    validationMapper,
  );

  const addressValue = useMemo(
    () => toUiAddress(formData, DomainAddressType.AddressPrevious),
    [formData],
  );

  const addressErrors = useMemo(
    () => addressErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const dateErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          fromMonth: formErrors.from_month?.message,
          fromYear: formErrors.from_year?.message,
          toMonth: formErrors.to_month?.message,
          toYear: formErrors.to_year?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyAddressChange = (patch: Partial<typeof addressValue>) => {
    const mapped = fromUiAddressPatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof PreviousAddressFormData)(val);
      }
    }
  };

  return (
    <SlideUpModal
      title="Add previous address"
      isOpen={true}
      openSession={address}
      closeHandler={closeHandler}
      hasDismissButton={true}
      actionButtonText={address?.id ? "Save" : "Add Address"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Previous Address"
        headerIcon={
          <Location06Icon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        }
      >
        <FormTableBody className="space-y-4">
          <AddressFields
            address={addressValue}
            errors={addressErrors}
            onFieldChange={(field, value) => {
              applyAddressChange({ [field]: value });
            }}
            onBulkChange={applyAddressChange}
            {...googlePlacesFieldProps}
          />

          <div className="space-y-3 border-t border-surface-container-disabled pt-4">
            <SectionTitle title="When did you begin living there?" size="md" />
            <p className="text-sm text-on-surface-muted">From</p>
            <Row>
              <Col>
                <Select
                  value={formData.from_month ?? ""}
                  placeholder="Month"
                  onValueChange={(value) => updateField("from_month")(value)}
                  error={!!dateErrors.fromMonth}
                  helperText={dateErrors.fromMonth}
                >
                  {month_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  value={formData.from_year ?? ""}
                  placeholder="Year"
                  onValueChange={(value) => updateField("from_year")(value)}
                  error={!!dateErrors.fromYear}
                  helperText={dateErrors.fromYear}
                >
                  {year_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
            </Row>
            <p className="text-sm text-on-surface-muted">To</p>
            <Row>
              <Col>
                <Select
                  value={formData.to_month ?? ""}
                  placeholder="Month"
                  onValueChange={(value) => updateField("to_month")(value)}
                  error={!!dateErrors.toMonth}
                  helperText={dateErrors.toMonth}
                >
                  {month_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  value={formData.to_year ?? ""}
                  placeholder="Year"
                  onValueChange={(value) => updateField("to_year")(value)}
                  error={!!dateErrors.toYear}
                  helperText={dateErrors.toYear}
                >
                  {year_data.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
